import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import { replace } from 'connected-react-router'
import { useInView } from 'react-intersection-observer'
import NavigableOfferContent from 'components/OfferPage/NavigableOfferContent/NavigableOfferContent'
import config from 'constants/config'
import {
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_V2,
  OFFER_TYPE_CRUISE_SLUG,
  OFFER_TYPE_TOUR_SLUG,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  PRODUCT_TYPE_ULTRALUX,
} from 'constants/offer'
import { TOP_YMAL } from 'constants/fetchOfferStrategy'
import { fetchAlternativeOffers, fetchOfferById, fetchTraderInformation, offerViewed } from 'actions/OfferActions'
import { offerProductDetailView } from 'actions/AnalyticsActions'
import { RouteComponentProps, withRouter } from 'react-router'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferContentLoader from 'components/OfferPage/OfferContentLoader'
import { BottomMobileRelatedTravelItems } from 'components/OfferPage/RelatedTravelItems'
import OfferPageHeadData from 'components/OfferPage/OfferPageHeadData'
import Heading from 'components/Luxkit/Typography/Heading'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import { fetchBedbankOfferById } from 'actions/BedbankOfferActions'
import {
  getPropertyBedbankOffer,
  getPropertyAlternateURL,
  getPropertyBestAlternateURL,
  getPropertyAlternateOfferIds,
} from 'selectors/offerPage/offerPageSelectors'
import { hasOrganicReferrer } from 'selectors/configSelectors'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { ENABLED_OFFER_TYPES_RECENTLY_VIEWED } from 'lib/offer/highIntentOffers'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import useLoadOfferBasedLereRecommendations from 'components/Recommendations/YouMayAlsoLike/Offer/hooks/useLoadOfferBasedLereRecommendations'
import FlashTourContent from './FlashTour/FlashTourContent'
import BundleAndSaveContent from './BundleAndSave/BundleAndSaveContent'
import UltraLuxContent from './UltraLux/UltraLuxContent'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { accommodationOfferClicked } from 'analytics/eventDefinitions'
import offerPageURL from 'lib/offer/offerPageURL'
import { isBundleOffer, isCruiseV1Offer, isLpcHotelOffer, isTourV1Offer, isVillaOffer } from 'lib/offer/offerTypes'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import OfferRecommendations from 'components/Common/OfferRecommendations/OfferRecommendations'
import BusinessTravellerStickyBudgetBar from 'businessTraveller/components/budget-bar/accommodation/BusinessTravellerStickyBudgetBar'
import zIndex from 'styles/tools/z-index'
import VillasPageContent from './Villas/VillasPageContent'
import CruiseRecommendations from 'components/Common/OfferRecommendations/CruiseRecommendations'
import { useLowRpvLere } from 'hooks/Recommendation/useLowRpvLere'
import { LERE_YMAL_STICKY_FOOTER_PAGE_SIZE } from 'components/Recommendations/YouMayAlsoLike/StickyFooter/YmalStickyFooter'
import HttpStatus from 'components/Common/HttpStatus'
import { usePlaceByName } from 'hooks/usePlace'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { usePageActivityTracking } from 'analytics/snowplow/helpers/useActivityTracking'
import { LuxPlusLabelProvider } from 'contexts/LuxPlusLabelContext'
import LuxPlusHiddenOfferOfferContent from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferOfferContent'
import { getIsLuxPlusHiddenOffer } from 'luxPlus/selectors/luxPlusHiddenOffer'
import HowItWorksItems from 'components/Common/HowItWorks/HowItWorksItems'
import Group from 'components/utils/Group'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import useCurrentSiteTakeover from 'hooks/useCurrentSiteTakeover'
import SiteTakeoverOfferPageBanner from 'components/Common/SiteTakeover/SiteTakeoverOfferPageBanner'
import useCruiseSearchFacets, { FACET_TYPES } from 'hooks/Cruise/useCruiseSearchFacets'
import LastMinuteOfferContent from './LastMinute/LastMinuteOfferContent'
import FlashHotelContainer from './FlashHotel/FlashHotelContainer'
import PropertyContainer from './Property/PropertyContainer'
import AlwaysOnContainer from './AlwaysOn/AlwaysOnContainer'
import { OptimizelyExperiments } from 'constants/optimizely'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import Sheet from 'components/utils/Sheet'
import useOffer from 'hooks/Offers/useOffer'

const StyledBusinessTravellerStickyBudgetBar = styled(BusinessTravellerStickyBudgetBar)`
  z-index: ${zIndex.floatingPanel};
`

const OfferPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const MainContent = styled(Group)`
  flex-grow: 1;
`

const ObservableWrapper = styled.div`
  padding-top: 1px;
  margin-top: ${rem(24)};
`

const StyledPane = styled(Pane)`
  position: relative;
`

interface Props extends RouteComponentProps<{
  offerSlug: string;
  offerId: string;
}> {
  offerId: string;
  traderInformation?: App.TraderInformation;
  regionCode: string;
  headlessMode: boolean;
  lereOfferStateAvailable: boolean;
  lereOfferCheckIn: string | undefined;
  lereOfferCheckOut: string | undefined;
  lereOfferDuration: number | undefined;
  lereOfferRooms: Array<App.Occupants> | undefined;
  propertyAlternateOfferIds?: App.AlternativeOffers;
  propertyBedbankOffer?: App.BedbankOffer;
  propertyAlternateUrl?: string;
  propertyBestAlternateUrl?: string;
  canDoSoldoutRedirect: boolean;
  relatedTravelItems: App.OfferState['relatedTravelItems'];
  propertyPageOfferId?: string;
  currency: string;
  isSpoofed: boolean;
  utmMedium: string | undefined;
}

const TOUR_HOLIDAY_TYPES = [OFFER_TYPE_TOUR_SLUG, OFFER_TYPE_CRUISE_SLUG]
const LERE_AB_TEST_MIN_MPV = 1.0

function generateFilters(
  offerType: App.OfferType,
  holidayTypes: Array<string> = [],
  locations: Array<string> = [],
  strategyApplied = '',
) {
  const filters = {} as any
  if (holidayTypes && holidayTypes.length > 0) {
    filters.holidayTypes = holidayTypes
  }
  if (locations && locations.length > 0) {
    filters.locations = locations
  }
  if (strategyApplied) {
    filters.strategyApplied = strategyApplied
  }
  if (offerType === OFFER_TYPE_TOUR) {
    filters.holidayTypes = TOUR_HOLIDAY_TYPES
  }
  return filters
}

function OfferPage(props: Props) {
  const {
    offerId,
    match,
    headlessMode,
    lereOfferStateAvailable,
    lereOfferCheckIn,
    lereOfferCheckOut,
    lereOfferDuration,
    lereOfferRooms,
    location: routerLocation,
    propertyAlternateOfferIds,
    propertyBedbankOffer,
    propertyAlternateUrl,
    propertyBestAlternateUrl,
    canDoSoldoutRedirect,
    relatedTravelItems,
    regionCode,
    traderInformation,
    propertyPageOfferId,
    currency,
    isSpoofed,
    utmMedium,
  } = props

  const [offer, fetching, offerError] = useOffer<App.HotelOffer | App.VillaOffer | App.TourOffer>(offerId)
  const propertyId = offer?.property?.id
  const [showNavOfferContent, setShowNavOfferContent] = useState(false)
  const dataLoaded = useRef(false)
  const dispatch = useAppDispatch()
  const isBusinessUser = config.businessTraveller.currentAccountMode === 'business'
  const isMobileScreen = useScreenSizeOnly('mobile')
  const isMobileDevice = useIsMobileDevice()
  const isHiddenOffer = useAppSelector(state => getIsLuxPlusHiddenOffer(state, offer))

  const [propertyMapping, fetchingMapper] = usePropertyOfferMapping(propertyId, {
    disabled: offer?.type !== OFFER_TYPE_ALWAYS_ON || !config.ACCOMM_USE_LPC_PROPERTY_PAGE,
  })

  // This one is used to load the section before the user gets to it
  const [navigableRef, navigableInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  const setNavigableRef = useCallback<React.RefCallback<HTMLDivElement>>(
    (node) => {
      navigableRef(node)
    },
    [navigableRef],
  )

  const offerTypeValidForLere = offer?.type && [
    OFFER_TYPE_HOTEL,
    OFFER_TYPE_ALWAYS_ON,
  ].includes(offer.type)

  useEffect(() => {
    if (offer) {
      // we use dataLoaded ref to prevent multiple analytics pushes
      fireEcommerceGTMEvent(accommodationOfferClicked(offer, currency))
      dataLoaded.current = true
    }
  }, [offer, currency])

  usePageActivityTracking()

  // LERE AB TEST FOR FILTERING LOW MPV OFFERS
  // Using selector here instead of direct useOptimizelyExperiment hook to avoid flashing/re-rendering the carousel as it triggers experiment when entire component is in view.
  const mpvAbTestVariation = useAppSelector(state =>
    getOptimizelyExperimentVariation(state, OptimizelyExperiments.lereFilterLowMpvCarousel),
  )

  // LERE graph recommendations fetch (Flash and LPC with Desktop only)
  useLoadOfferBasedLereRecommendations(
    offer?.id,
    !!offerTypeValidForLere && lereOfferStateAvailable,
    lereOfferCheckIn,
    lereOfferCheckOut,
    lereOfferDuration,
    lereOfferRooms,
  )

  useEffect(() => {
    if (!showNavOfferContent && routerLocation.hash) {
      setShowNavOfferContent(true)
    }
  }, [routerLocation.hash]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showNavOfferContent) {
      window.location.href = routerLocation.hash
    }
  }, [showNavOfferContent]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (match.params.offerId) {
      dispatch(fetchOfferById(match.params.offerId))
    } else if (propertyPageOfferId) {
      dispatch(fetchOfferById(propertyPageOfferId))
    }
  }, [dispatch, match.params.offerId, propertyPageOfferId])

  useEffect(() => {
    if (offer && config.TRADER_INFORMATION_REGIONS && isEnabledForFeature(config.TRADER_INFORMATION_REGIONS, regionCode)) {
      dispatch(fetchTraderInformation(offer.id, regionCode))
    }
  }, [dispatch, offer?.id, regionCode]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (offer) {
      dispatch(offerProductDetailView(offer))
      window.dataLayer?.push({ offerType: offer.type })
    }
  }, [offer?.id, utmMedium]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (propertyId && !propertyAlternateOfferIds) {
      dispatch(fetchAlternativeOffers(propertyId, 'le'))
    } else if (!!propertyAlternateOfferIds?.bedbank?.length && !propertyBedbankOffer) {
      dispatch(fetchBedbankOfferById(propertyAlternateOfferIds.bedbank[0]))
    }
  }, [propertyId, propertyAlternateOfferIds, propertyBedbankOffer, dispatch])

  useEffect(() => {
    if (!!offer && ENABLED_OFFER_TYPES_RECENTLY_VIEWED.has(offer.type)) {
      dispatch(offerViewed(offer.id, offer.type))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.id, dispatch])

  const onDatePickerClose = useCallback(() => {
    if (routerLocation.hash) {
      dispatch(replace({ ...routerLocation, hash: undefined }))
    }
  }, [dispatch, routerLocation])

  const recommendationFilters: App.OfferListFilters = useMemo(() => {
    const baseFilters = (!offerError && !fetching && offer) ? generateFilters(
      offer.type,
      offer.holidayTypes,
      offer.locations,
      TOP_YMAL,
    ) : {}
    const retFilters = {
      ...baseFilters,
      checkIn: lereOfferCheckIn,
      checkOut: lereOfferCheckOut,
      rooms: lereOfferRooms,
      lere: 'graph',
      ...(offer?.id && { baseOfferId: offer.id }),
    }
    if (config.BRAND === 'scoopontravel' && propertyId) {
      retFilters.propertyId = 'le:' + propertyId
      retFilters.locations = undefined
      retFilters.strategyApplied = undefined
      retFilters.searchNearby = true
      retFilters.lere = undefined
    }

    // LERE AB TEST FILTER LOW MPV OFFERS: only include minMpv in variant B
    if (mpvAbTestVariation === 'on') {
      retFilters.minMpv = LERE_AB_TEST_MIN_MPV
    }

    return retFilters
  }, [fetching, lereOfferCheckIn, lereOfferCheckOut, lereOfferRooms, offer, offerError, propertyId, mpvAbTestVariation])

  const cruiseLocation = useMemo(() => {
    if (isCruiseV1Offer(offer)) {
      return offer.startLocation ?? [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
    }
  }, [offer])

  const cruisePlace = usePlaceByName(cruiseLocation)
  const siteTakeover = useCurrentSiteTakeover({ vertical: 'hotel', offer: isTourV1Offer(offer) ? undefined : offer })

  const [cruiseLines] = useCruiseSearchFacets({ facetTypes: [FACET_TYPES.CRUISE_LINES] })
  const isCruise = offer?.holidayTypes?.includes('Cruises')
  const recommendationFiltersCruise = useMemo<App.OfferListFilters>(() => {
    if (isCruise && offer) {
      const cruiseLine = cruiseLines.find(({ name }) =>
        offer.vendorName && name.includes(offer.vendorName.split(' ')[0]) ||
        offer.property && name.includes(offer.property?.name.split(' ')[0]))

      return {
        offerTypes: ['cruise'],
        departurePlaceId: cruisePlace?.id,
        cruiseLines: cruiseLine ? [cruiseLine.code!] : undefined,
      }
    }
    return {}
  }, [cruiseLines, cruisePlace?.id, isCruise, offer])

  // After AB test, the LERE sticky footer of LPC offer pages with low RPV has better result on mobile web
  // So keep the LERE sticky footer for mobile web only
  const lereYmalStickyFooterShowTrigger = useInView({ triggerOnce: true })
  const canUseLereStickyFooter = isMobileScreen && !isBusinessUser && !isSpoofed && isLpcHotelOffer(offer) && lereYmalStickyFooterShowTrigger.inView
  const shouldUseLereStickyFooter = useLowRpvLere(offer?.id ?? '', canUseLereStickyFooter)

  if (offerError) {
    if (offerError?.status === 404) {
      return <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>
    } else {
      return <HttpStatus code={500}>
        <Error500Page error={offerError} />
      </HttpStatus>
    }
  }

  if (fetching || fetchingMapper || !offer) {
    return <OfferContentLoader />
  }

  if (isHiddenOffer) {
    return (
      <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
        <OfferPageHeadData offer={offer} isLuxPlusHiddenOffer />
        <LuxPlusHiddenOfferOfferContent offer={offer} />
      </AnalyticsPageContext.Provider>
    )
  }

  const isTourOffer = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2].includes(offer.parentType)
  const isVilla = offer.type === 'rental'
  const breadCrumbItems = relatedTravelItems[offer?.id] || []
  const isBundleAndSave = isBundleOffer(offer)

  const {
    id,
    onlinePurchaseSchedule,
    type,
  } = offer

  const howItWorksItems = config.offerList.howItWorks[type as 'tour' | 'hotel' | 'last_minute_hotel']

  const availableForPurchase = scheduleIsCurrent(onlinePurchaseSchedule)
  let OfferPageContent: any

  if (offer.type === 'last_minute_hotel') {
    OfferPageContent = LastMinuteOfferContent
  } else if (offer.type === 'hotel' || offer.type === 'tactical_ao_hotel') {
    if (offer.property.isUltraLux) {
      OfferPageContent = UltraLuxContent
    } else if (offer.type === OFFER_TYPE_HOTEL) {
      OfferPageContent = FlashHotelContainer
    } else if (offer.type === OFFER_TYPE_ALWAYS_ON) {
      if (propertyMapping) {
        OfferPageContent = PropertyContainer
      } else {
        OfferPageContent = AlwaysOnContainer
      }
    }
  } else if (isBundleAndSave) {
    OfferPageContent = BundleAndSaveContent
  } else if (isTourV1Offer(offer)) {
    OfferPageContent = FlashTourContent
  } else if (isVillaOffer(offer)) {
    OfferPageContent = VillasPageContent
  }

  if (offer.slug !== match.params.offerSlug) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }

  if (offer.type !== 'hotel' && propertyBestAlternateUrl) {
    return <RedirectWithStatus code={301} to={propertyBestAlternateUrl} />
  }

  if (!availableForPurchase && canDoSoldoutRedirect && propertyAlternateUrl) {
    return <RedirectWithStatus code={301} to={propertyAlternateUrl} />
  }

  return <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
    <OfferPageHeadData offer={offer} />
    <StyledBusinessTravellerStickyBudgetBar
      checkInDate={lereOfferCheckIn}
      checkOutDate={lereOfferCheckOut}
      offer={offer}
      roomCount={lereOfferRooms?.length}
    />
    <OfferPageWrapper className="OfferPage">
      {!!siteTakeover?.offerTypes?.has(offer.type) && <SiteTakeoverOfferPageBanner />}
      <MainContent direction="vertical" verticalAlign="space-between">
        <LuxPlusLabelProvider page="offer-page">
          <OfferPageContent
            offer={offer}
            key={offer.id}
            onFilterClose={onDatePickerClose}
            shouldUseLereStickyFooter={shouldUseLereStickyFooter}
            lereYmalStickyFooterShowTrigger={lereYmalStickyFooterShowTrigger}
          />
        </LuxPlusLabelProvider>
        <SoftOfferPageWalledGarden bypassWall={[OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK, OFFER_TYPE_ALWAYS_ON].includes(offer.type)}>
          <ObservableWrapper ref={setNavigableRef}>
            {!isVilla && <StyledPane>
              {(navigableInView || showNavOfferContent) && (
                <>
                  <NavigableOfferContent
                    offerDetails={offer}
                    traderInformation={traderInformation}
                    hash={routerLocation.hash}
                    mobileHotelOfferPageTest={isMobileDevice}
                  />
                  {howItWorksItems && !offer.bundledWithFlightsOnly && !offer.disableBestPriceGuarantee && offer.productType !== PRODUCT_TYPE_ULTRALUX && <Sheet padding="40 0">
                    <LayoutContainer>
                      <Pane border="neutral-five" borderRadius="S" padding="40 24">
                        <Group direction="vertical" gap={32}>
                          <Heading variant="heading3" align="center">
                            Why you should book with us
                          </Heading>
                          <HowItWorksItems items={howItWorksItems} />
                        </Group>
                      </Pane>
                    </LayoutContainer>
                  </Sheet>}
                </>
              )}
            </StyledPane>}
          </ObservableWrapper>
          <ErrorBoundary fallback={null}>
            {!isCruise && !isVilla && !isBundleAndSave && !isTourOffer && !isBusinessUser && !isSpoofed &&
              <OfferRecommendations
                title={`Similar to ${offer.property?.name ?? 'this'}`}
                filters={recommendationFilters}
                offerId={id}
                offerType={offer?.type}
                trackingDescription="Offer LE"
                skipFirstNOffers={shouldUseLereStickyFooter ? LERE_YMAL_STICKY_FOOTER_PAGE_SIZE : undefined} // dedup reco with footer
                enableDateCheckingForPriceDisplay
              />
            }
          </ErrorBoundary>
          <ErrorBoundary fallback={null}>
            {isTourV1Offer(offer) && recommendationFiltersCruise.departurePlaceId && !isBusinessUser && !isSpoofed &&
              <CruiseRecommendations
                filters={recommendationFiltersCruise}
                offer={offer}
                cruiseLineName={offer.vendorName}
                departurePortName={offer.startLocation}
              />
            }
          </ErrorBoundary>
          {!headlessMode &&
            <BottomMobileRelatedTravelItems offer={offer} items={breadCrumbItems} />
          }
        </SoftOfferPageWalledGarden>
      </MainContent>
    </OfferPageWrapper>
  </AnalyticsPageContext.Provider>
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  const offerId = ownProps.propertyPageOfferId || ownProps.match?.params?.offerId || ''
  const offerSharedState = state.recommendations.offerSharedState[offerId]
  return {
    offerId,
    regionCode: state.geo.currentRegionCode,
    canDoSoldoutRedirect: IS_SSR && hasOrganicReferrer(state),
    traderInformation: state.offer.traderInformation[offerId]?.data,
    headlessMode: state.system.headlessMode,
    propertyAlternateOfferIds: getPropertyAlternateOfferIds(state, offerId),
    propertyBedbankOffer: getPropertyBedbankOffer(state, offerId),
    propertyAlternateUrl: getPropertyAlternateURL(state, offerId),
    propertyBestAlternateUrl: IS_SSR && getPropertyBestAlternateURL(state, offerId),
    relatedTravelItems: state.offer.relatedTravelItems,
    lereOfferStateAvailable: !!offerSharedState,
    lereOfferCheckIn: offerSharedState?.checkIn,
    lereOfferCheckOut: offerSharedState?.checkOut,
    lereOfferDuration: offerSharedState?.duration,
    lereOfferRooms: offerSharedState?.rooms,
    currency: state.geo.currentCurrency,
    isSpoofed: state.auth.account.isSpoofed,
    utmMedium: state.utm.utmMedium,
  }
}

type MappedValues = ReturnType<typeof mapStateToProps>

export default withRouter<Omit<Props, keyof MappedValues>, any>(connect(mapStateToProps)(OfferPage))
